<template>
  <div>
    <!-- Page header -->
    <CCard class="mb-4">
      <CCardBody class="p-4">
        <CButton color="link" class="px-0 mb-2" @click="cancel">
          <CIcon name="cil-arrow-left" class="mr-1" /> Businesses
        </CButton>
        <h1 class="h4 mb-1">{{ isNewApplication() ? "Add business" : obj.name || "Business" }}</h1>
        <p class="text-muted mb-0">
          Company details, branding and document footers used on your invoices, quotations and receipts
        </p>
      </CCardBody>
    </CCard>
    <CRow>
      <CCol sm="12">
        <CCard>
          <CCardBody>
            <CTabs add-tab-classes="mt-1">
              <CTab active>
                <template slot="title">
                  <CIcon name="cil-calculator" /> General
                </template>
                <CCard>
                  <CForm @submit.prevent="submit">
                    <CCardBody>
                      <!-- Group related fields for better UX -->
                      <CRow form>
                        <CCol md="6">
                          <CInput
                            label="Company name *"
                            v-model="obj.name"
                            placeholder="XYZ Sdn Bhd"
                            required
                            was-validated
                          />
                          <CInput
                            label="Registration no. *"
                            v-model="obj.regNo"
                            placeholder="123456-V"
                            required
                            was-validated
                          />
                          <CInput
                            label="BRN, old format (optional)"
                            v-model="obj.brnOld"
                            placeholder="123456-V"
                            description="Business registration number in the old format"
                          />
                          <CInput
                            label="BRN, new format (optional)"
                            v-model="obj.brnNew"
                            placeholder="202001234567"
                            description="12-digit business registration number"
                          />
                          <CInput
                            label="Tagline (optional)"
                            v-model="obj.tagLine"
                            placeholder="Your Success, Our Commitment."
                          />
                          <CInput
                            v-model="obj.taxIdentificationNumber"
                            placeholder="C1234567890"
                          >
                            <template #label>
                              <label>
                                TIN
                                <CIcon
                                  name="cil-info"
                                  size="sm"
                                  class="text-muted ml-1"
                                  tabindex="0"
                                  aria-label="What is TIN?"
                                  v-c-tooltip="{
                                    content: 'Tax Identification Number issued by LHDN.',
                                    placement: 'top',
                                  }"
                                />
                              </label>
                            </template>
                          </CInput>
                          <CInput
                            v-model="obj.sstNumber"
                            placeholder="A01-1234-56789012"
                          >
                            <template #label>
                              <label>
                                SST number (optional)
                                <CIcon
                                  name="cil-info"
                                  size="sm"
                                  class="text-muted ml-1"
                                  tabindex="0"
                                  aria-label="What is SST?"
                                  v-c-tooltip="{
                                    content: 'Sales and Service Tax charged on taxable goods and services.',
                                    placement: 'top',
                                  }"
                                />
                              </label>
                            </template>
                          </CInput>
                          <!-- Business.SstInputTaxClaimable — see docs/ACCOUNTING-POSTING.md "SST on purchases" -->
                          <div class="form-group">
                            <label class="d-block">SST on purchases is claimable</label>
                            <div class="d-flex align-items-center">
                              <CSwitch
                                color="primary"
                                :checked.sync="obj.sstInputTaxClaimable"
                                aria-label="SST on purchases is claimable"
                              />
                              <span class="ml-2">{{ obj.sstInputTaxClaimable ? "Yes" : "No" }}</span>
                            </div>
                            <small class="form-text text-muted">
                              Most businesses can't claim back SST paid on purchases, so it's counted as a cost.
                              Switch this on only if your tax agent confirms you can. Existing tax periods update
                              when you press Recalculate on them.
                            </small>
                          </div>
                          <CInput
                            label="Date of incorporation"
                            type="date"
                            v-model="obj.dateIncorporation"
                          />
                          <CInput
                            v-model="obj.msicCode"
                            placeholder="46510"
                            inputmode="numeric"
                          >
                            <template #label>
                              <label>
                                MSIC code
                                <CIcon
                                  name="cil-info"
                                  size="sm"
                                  class="text-muted ml-1"
                                  tabindex="0"
                                  aria-label="What is an MSIC code?"
                                  v-c-tooltip="{
                                    content: 'Malaysia Standard Industrial Classification code that describes your business activity.',
                                    placement: 'top',
                                  }"
                                />
                              </label>
                            </template>
                          </CInput>
                          <CSelect
                            label="Currency"
                            :options="currencyOptions"
                            :value.sync="obj.currency"
                          />
                          <CInput
                            label="Website"
                            v-model="obj.website"
                            placeholder="www.xyz.com.my"
                          />
                          <CInput
                            label="Phone"
                            type="tel"
                            v-model="obj.phone"
                            placeholder="03-897654321"
                          />
                        </CCol>
                        <CCol md="6">
                          <CInput
                            label="Short name *"
                            v-model="obj.shortName"
                            placeholder="XYZ"
                            description="Used where space is tight, such as menus and reports"
                            required
                            was-validated
                          />
                          <CTextarea
                            label="Address *"
                            placeholder="No. 123, Jalan Example
Taman Example, 
12345 Kuala Lumpur,
Malaysia"
                            rows="5"
                            v-model="obj.address"
                            required
                            was-validated
                          />
                          <!-- ASEAN Country Selection -->
                          <CSelect
                            :value.sync="obj.country"
                            :options="aseanCountries"
                            label="Country"
                          />
                          <!-- State Selection -->
                          <CSelect
                            :value.sync="obj.state"
                            :options="stateOptions"
                            label="State"
                          />
                          <!-- District Selection -->
                          <CSelect
                            :value.sync="obj.district"
                            :options="statesAndDistricts[obj.state]"
                            label="District"
                          />

                          <CInput
                            label="Postcode"
                            v-model="obj.postcode"
                            placeholder="12345"
                            inputmode="numeric"
                          />
                        </CCol>
                      </CRow>
                      <CRow>
                        <CCol md="6"
                          ><CFormGroup wrapperClasses="input-group pt-2">
                            <template #label> Company logo </template>
                            <template #input>
                              <div style="width: 100%">
                                <CRow>
                                  <CCol sm="6">
                                    <CImg
                                      :src="getImageUrl()"
                                      class="mb-2"
                                      thumbnail
                                      responsive
                                    />
                                  </CCol>
                                  <CCol sm="6"
                                    ><WidgetsUploadImage
                                      :logoUrl="logoUrl"
                                      @uploaded="uploaded"
                                  /></CCol>
                                </CRow>
                              </div>
                            </template> </CFormGroup
                        ></CCol>
                        <CCol md="6"
                          ><CFormGroup wrapperClasses="input-group pt-2">
                            <template #label> Company stamp (chop) </template>
                            <template #input>
                              <div style="width: 100%">
                                <CRow>
                                  <CCol sm="6">
                                    <CImg
                                      :src="getChopImageUrl()"
                                      class="mb-2"
                                      thumbnail
                                      responsive
                                    />
                                  </CCol>
                                  <CCol sm="6"
                                    ><WidgetsUploadImage
                                      :logoUrl="chopUrl"
                                      @uploaded="chop_uploaded"
                                  /></CCol>
                                </CRow>
                              </div>
                            </template> </CFormGroup
                        ></CCol>
                      </CRow>
                      <CRow>
                        <CCol sm="12">
                          <CTextarea
                            label="Quotation footer"
                            placeholder=""
                            rows="5"
                            v-model="obj.quotationDefaultFooter"
                          />
                        </CCol>
                      </CRow>
                      <CRow>
                        <CCol sm="12">
                          <CTextarea
                            label="Invoice footer"
                            placeholder=""
                            rows="5"
                            v-model="obj.invoiceDefaultFooter"
                          />
                        </CCol>
                      </CRow>
                      <CRow>
                        <CCol sm="12">
                          <CTextarea
                            label="Receipt footer"
                            placeholder=""
                            rows="5"
                            v-model="obj.receiptDefaultFooter"
                          />
                        </CCol>
                      </CRow>
                      <CRow>
                        <CCol sm="12">
                          <hr class="mt-3 mb-3" />
                          <h2 class="h6">Payslip settings</h2>
                        </CCol>
                      </CRow>
                      <CRow>
                        <CCol sm="12">
                          <CInputCheckbox
                            label="Create payslips automatically when a payroll is approved"
                            :checked.sync="obj.autoGeneratePayslipOnApproval"
                            :custom="true"
                          />
                          <small class="form-text text-muted">
                            When this is on, approving a payroll creates a payslip for each employee, so you
                            don't have to create them yourself.
                          </small>
                        </CCol>
                      </CRow>
                    </CCardBody>
                    <CCardFooter class="d-flex justify-content-between">
                      <CButton color="light" :disabled="saving" @click="cancel">
                        Cancel
                      </CButton>
                      <CButton
                        type="submit"
                        color="primary"
                        :disabled="!isFormValid || saving"
                      >
                        <CSpinner v-if="saving" size="sm" class="mr-1" />
                        {{ isNewApplication() ? "Add business" : "Save" }}
                      </CButton>
                    </CCardFooter>
                  </CForm>
                </CCard>
              </CTab>

              <CTab v-if="!isNewApplication()">
                <template slot="title">
                  <CIcon name="cil-people" /> Users
                </template>
                <CCard>
                  <CCardHeader class="d-flex justify-content-between align-items-center">
                    <h2 class="h6 mb-0">Users</h2>
                    <CButton color="primary" @click="addNewProfile">
                      <CIcon name="cil-plus" /> Add user
                    </CButton>
                  </CCardHeader>
                  <CCardBody>
                    <div v-if="profileLoading && !profileItems.length" class="text-center p-5">
                      <CSpinner color="primary" />
                    </div>
                    <div v-else-if="profileError && !profileItems.length" class="text-center p-5">
                      <CIcon name="cil-warning" size="xl" class="text-danger mb-2" />
                      <h3 class="h6">Couldn't load users</h3>
                      <p class="text-muted">Check your connection and try again.</p>
                      <CButton color="primary" variant="outline" @click="refreshProfileTable">
                        <CIcon name="cil-reload" /> Retry
                      </CButton>
                    </div>
                    <div v-else-if="!profileItems.length" class="text-center p-5">
                      <CIcon name="cil-people" size="xl" class="text-muted mb-2" />
                      <h3 class="h6">No users yet</h3>
                      <p class="text-muted">Add accountants or staff who can work in this business.</p>
                      <CButton color="primary" @click="addNewProfile">
                        <CIcon name="cil-plus" /> Add user
                      </CButton>
                    </div>
                    <template v-else>
                      <div class="d-none d-lg-block">
                        <CDataTable
                          :items="computedProfileItems"
                          :fields="profilefields"
                          column-filter
                          items-per-page-select
                          :items-per-page="10"
                          hover
                          sorter
                          pagination
                          :loading="profileLoading"
                        >
                          <template #show_details="{ item }">
                            <td class="text-right text-nowrap">
                              <CButton
                                color="primary"
                                variant="ghost"
                                :aria-label="`Edit ${item.fullName}`"
                                @click="onEditProfile(item)"
                              >
                                <CIcon name="cil-pencil" />
                              </CButton>
                              <CButton
                                color="danger"
                                variant="ghost"
                                :aria-label="`Remove ${item.fullName}`"
                                @click="showDeleteProfileConfirmation(item)"
                              >
                                <CIcon name="cil-trash" />
                              </CButton>
                            </td>
                          </template>
                        </CDataTable>
                      </div>
                      <div class="d-lg-none">
                        <CCard
                          v-for="item in computedProfileItems"
                          :key="item.id"
                          class="mb-2"
                        >
                          <CCardBody class="p-3 d-flex justify-content-between align-items-start">
                            <div>
                              <div class="font-weight-bold">{{ item.fullName }}</div>
                              <div class="small text-muted">{{ item.email }}</div>
                              <div class="small text-muted">{{ item.role }}</div>
                            </div>
                            <div class="text-nowrap">
                              <CButton
                                color="primary"
                                variant="ghost"
                                :aria-label="`Edit ${item.fullName}`"
                                @click="onEditProfile(item)"
                              >
                                <CIcon name="cil-pencil" />
                              </CButton>
                              <CButton
                                color="danger"
                                variant="ghost"
                                :aria-label="`Remove ${item.fullName}`"
                                @click="showDeleteProfileConfirmation(item)"
                              >
                                <CIcon name="cil-trash" />
                              </CButton>
                            </div>
                          </CCardBody>
                        </CCard>
                      </div>
                    </template>
                  </CCardBody>
                </CCard>
                <CModal
                  :show.sync="warningProfileModal"
                  :centered="true"
                  :close-on-backdrop="!deletingProfile"
                >
                  <template #header>
                    <h5 class="modal-title">Remove {{ itemToDelete.fullName }}?</h5>
                  </template>
                  <p class="mb-0">
                    {{ itemToDelete.fullName }} ({{ itemToDelete.email }}) will lose access to this
                    business. This can't be undone.
                  </p>
                  <template #footer>
                    <CButton color="light" :disabled="deletingProfile" @click="warningProfileModal = false">
                      Keep user
                    </CButton>
                    <CButton color="danger" :disabled="deletingProfile" @click="confirmDeleteProfile">
                      <CSpinner v-if="deletingProfile" size="sm" class="mr-1" />
                      Remove user
                    </CButton>
                  </template>
                </CModal>
                <CModal
                  :show.sync="editProfileModal"
                  :close-on-backdrop="false"
                  :centered="true"
                  size="lg"
                >
                  <CInput label="Name *" horizontal v-model="profile.fullName" />

                  <CSelect
                    label="Role"
                    horizontal
                    :options="['Accountant', 'Staff']"
                    :value.sync="profile.appUser.role"
                  />
                  <CInput
                    label="Email *"
                    type="email"
                    horizontal
                    v-model="profile.email"
                    :readonly="isEmailReadOnly"
                  />

                  <CRow form class="form-group">
                    <CCol tag="label" sm="3" class="col-form-label">
                      Set a new password
                    </CCol>
                    <CCol sm="9">
                      <CSwitch
                        class="mr-1"
                        color="primary"
                        :checked.sync="isResetPassword"
                        aria-label="Set a new password"
                      />
                    </CCol>
                  </CRow>
                  <CInput
                    description="At least 8 characters, with a capital letter, a number and a special character."
                    label="Password *"
                    type="password"
                    horizontal
                    autocomplete="new-password"
                    v-model="plainPassword"
                    v-if="isResetPassword"
                  />
                  <CRow form class="form-group">
                    <CCol tag="label" sm="3" class="col-form-label">
                      Can sign in
                    </CCol>
                    <CCol sm="9">
                      <CSwitch
                        class="mr-1"
                        color="primary"
                        :checked.sync="profile.isEnable"
                        aria-label="Can sign in"
                      />
                    </CCol>
                  </CRow>

                  <template #header>
                    <h5 class="modal-title">{{ profile.id ? "Edit user" : "Add user" }}</h5>
                    <CButtonClose
                      aria-label="Close"
                      @click="editProfileModal = false"
                    />
                  </template>
                  <template #footer>
                    <CButton color="light" :disabled="savingProfile" @click="editProfileModal = false"
                      >Cancel</CButton
                    >
                    <CButton color="primary" :disabled="savingProfile" @click="updateProfile">
                      <CSpinner v-if="savingProfile" size="sm" class="mr-1" />
                      {{ profile.id ? "Save" : "Add user" }}
                    </CButton>
                  </template>
                </CModal>
              </CTab>
            </CTabs>
          </CCardBody>
        </CCard>
      </CCol>
    </CRow>
  </div>
</template>


<script>
import { asDateString } from "@/utils/datetime";
import BusinessApi from "@/lib/businessApi";
import WidgetsUploadImage from "../widgets/WidgetsUploadImage";
import ProfileApi from "@/lib/profileApi";

const profilefields = [
  { key: "fullName", label: "Name" },
  { key: "email", label: "Email" },
  { key: "business", label: "Default business" },
  { key: "role", label: "Role" },
  {
    key: "show_details",
    label: "",
    _style: "width:150px",
    sorter: false,
    filter: false,
  },
];

export default {
  name: "Business",
  components: {
    WidgetsUploadImage,
  },
  data() {
    return {
      currencyOptions: [
  { value: 'MYR', label: 'MYR - Ringgit Malaysia' },
  { value: 'SGD', label: 'SGD - Singapore Dollar' },
  { value: 'USD', label: 'USD - US Dollar' },
  { value: 'THB', label: 'THB - Thai Baht' },
  { value: 'IDR', label: 'IDR - Indonesian Rupiah' },
  { value: 'VND', label: 'VND - Vietnamese Dong' },
  { value: 'PHP', label: 'PHP - Philippine Peso' },
],
      isResetPassword: false,
      plainPassword: "",
      isEmailReadOnly: false,

      profile: {
        fullName: "",
        appUser: {
          role: "Staff",
        },
        email: "",
        isEnable: true,
      },
      editProfileModal: false,

      itemToDelete: {},
      showQuickInfo: false,
      profileApi: new ProfileApi(),
      warningProfileModal: false,
      deletingProfile: false,
      savingProfile: false,
      saving: false,
      profileError: false,
      profileLoading: true,
      profileItems: [],
      profilefields,

      selectedState: "",
      selectedDistrict: "",
      selectedCountry: "",
      statesAndDistricts: {
        Johor: [
          "Batu Pahat",
          "Johor Bahru",
          "Kluang",
          "Kota Tinggi",
          "Mersing",
          "Muar",
          "Pontian",
          "Segamat",
          "Tangkak",
          "Kulai",
        ],
        Kedah: [
          "Baling",
          "Bandar Baharu",
          "Kota Setar",
          "Kuala Muda",
          "Kubang Pasu",
          "Kulim",
          "Langkawi",
          "Padang Terap",
          "Pendang",
          "Pokok Sena",
          "Sik",
          "Yan",
        ],
        Kelantan: [
          "Bachok",
          "Gua Musang",
          "Jeli",
          "Kota Bharu",
          "Kuala Krai",
          "Machang",
          "Pasir Mas",
          "Pasir Puteh",
          "Tanah Merah",
          "Tumpat",
        ],
        Malacca: ["Alor Gajah", "Jasin", "Central Melaka"],
        "Negeri Sembilan": [
          "Jelebu",
          "Jempol",
          "Kuala Pilah",
          "Port Dickson",
          "Rembau",
          "Seremban",
          "Tampin",
        ],
        Pahang: [
          "Bentong",
          "Bera",
          "Cameron Highlands",
          "Jerantut",
          "Kuantan",
          "Lipis",
          "Maran",
          "Pekan",
          "Raub",
          "Rompin",
          "Temerloh",
        ],
        Penang: [
          "Seberang Perai Selatan",
          "Seberang Perai Tengah",
          "Seberang Perai Utara",
          "Timur Laut",
          "Barat Daya",
        ],
        Perak: [
          "Batang Padang",
          "Hilir Perak",
          "Hulu Perak",
          "Kinta",
          "Kerian",
          "Kuala Kangsar",
          "Larut, Matang and Selama",
          "Manjung",
          "Perak Tengah",
          "Kampar",
          "Muallim",
          "Bagan Datuk",
        ],
        Perlis: ["Kangar", "Padang Besar", "Arau"],
        Sabah: [
          "Beaufort",
          "Beluran",
          "Kalabakan",
          "Keningau",
          "Kinabatangan",
          "Kota Belud",
          "Kota Kinabalu",
          "Kota Marudu",
          "Kuala Penyu",
          "Kudat",
          "Kunak",
          "Lahad Datu",
          "Nabawan",
          "Papar",
          "Penampang",
          "Pitas",
          "Putatan",
          "Ranau",
          "Sandakan",
          "Semporna",
          "Sipitang",
          "Tambunan",
          "Tawau",
          "Telupid",
          "Tenom",
          "Tongod",
          "Tuaran",
        ],
        Sarawak: [
          "Asajaya",
          "Bau",
          "Belaga",
          "Betong",
          "Bintulu",
          "Dalat",
          "Daro",
          "Julau",
          "Kapit",
          "Kuching",
          "Lawas",
          "Limbang",
          "Lubok Antu",
          "Lundu",
          "Maradong",
          "Matu",
          "Miri",
          "Mukah",
          "Pakan",
          "Pusa",
          "Samarahan",
          "Sarikei",
          "Sebauh",
          "Selangau",
          "Serian",
          "Sibu",
          "Simunjan",
          "Song",
          "Sri Aman",
          "Subis",
          "Tatau",
          "Tebedu",
        ],
        Selangor: [
          "Gombak",
          "Hulu Langat",
          "Hulu Selangor",
          "Klang",
          "Kuala Langat",
          "Kuala Selangor",
          "Petaling",
          "Sabak Bernam",
          "Sepang",
        ],
        Terengganu: [
          "Besut",
          "Dungun",
          "Hulu Terengganu",
          "Kemaman",
          "Kuala Nerus",
          "Kuala Terengganu",
          "Marang",
          "Setiu",
        ],
        "Kuala Lumpur": [
          "Cheras",
          "Kepong",
          "Lembah Pantai",
          "Seputeh",
          "Setiawangsa",
          "Titiwangsa",
          "Wangsa Maju",
          "Bukit Bintang",
        ],
        Labuan: ["Victoria"],
        Putrajaya: [
          "Presint 1",
          "Presint 2",
          "Presint 3",
          "Presint 4",
          "Presint 5",
          "Presint 6",
          "Presint 7",
          "Presint 8",
          "Presint 9",
          "Presint 10",
          "Presint 11",
          "Presint 12",
          "Presint 13",
          "Presint 14",
          "Presint 15",
          "Presint 16",
          "Presint 17",
          "Presint 18",
          "Presint 19",
          "Presint 20",
        ],
      },
      aseanCountries: [
        "Brunei",
        "Cambodia",
        "Indonesia",
        "Laos",
        "Malaysia",
        "Myanmar",
        "Philippines",
        "Singapore",
        "Thailand",
        "Vietnam",
      ],

      logoUrl: "",
      chopUrl: "",
      obj: {
        currency: "RM",
        country: "Malaysia",
        state: "Selangor",
        district: "Hulu Langat",
      },
      submitted: false,
      api: new BusinessApi(),
    };
  },
  mounted() {
    this.resetObj();
    this.refreshProfileTable();
  },

  computed: {
    computedProfileItems() {
      return this.profileItems.map((item) => {
        return {
          ...item,
          business: this.getProfileBusinessName(item),
          role: item.appUser.role,
        };
      });
    },

    stateOptions() {
      // Extract and sort state names alphabetically
      return Object.keys(this.statesAndDistricts)
        .sort((a, b) => a.localeCompare(b)) // Sort alphabetically
        .map((state) => ({
          label: state,
          value: state,
        }));
    },
    isFormValid() {
      // Example logic to check form validity
      return this.obj.name && this.obj.regNo && this.obj.address;
    },
    computedLogoUrl() {
      return this.removeTrailingSlash(apiUrl) + this.obj.logoUrl;
    },
  },
  methods: {
    updateProfile() {
      this.addOrUpdateProfile(this.profile);
    },
    addOrUpdateProfile(editObj) {
      var self = this;
      var profileDto = {
        profile: editObj,
        isResetPassword: self.isResetPassword,
        plainPassword: self.plainPassword,
        role: editObj.appUser.role,
      };

      self.savingProfile = true;
      const request = !editObj.id
        ? this.profileApi.createProfileEmployee(profileDto)
        : this.profileApi.updateProfileAppUser(profileDto);
      request
        .then(() => {
          self.$toast.success(editObj.id ? "User saved" : "User added");
          self.refreshProfileTable();
          self.editProfileModal = false;
        })
        .catch(({ data }) => {
          self.$toast.error(
            `Couldn't save the user. ${helper.getErrorMessage(data)}`
          );
        })
        .finally(() => {
          self.savingProfile = false;
        });
    },

    getProfileBusinessName(item) {
      try {
        return item.defaultBusiness.name;
      } catch (error) {
        return "—";
      }
    },
    /// For Profile List
    onEditProfile(item) {
      this.profile = item;
      this.isResetPassword = false;
      this.editProfileModal = true;
    },
    confirmDeleteProfile() {
      var self = this;
      self.deletingProfile = true;
      this.profileApi
        .deleteProfile(self.itemToDelete.id)
        .then(() => {
          self.$toast.success("User removed");
          self.warningProfileModal = false;
          self.itemToDelete = {};
          self.refreshProfileTable();
        })
        .catch(({ data }) => {
          self.$toast.error(
            `Couldn't remove the user. ${helper.getErrorMessage(data)}`
          );
        })
        .finally(() => {
          self.deletingProfile = false;
        });
    },
    showDeleteProfileConfirmation(item) {
      var self = this;
      self.itemToDelete = item;
      self.warningProfileModal = true;
    },
    addNewProfile() {
      this.profile = {
        fullName: "",
        appUser: {
          role: "Staff",
        },
        email: "",
        isEnable: true,
        defaultBusinessId: this.obj.id,
      };
      this.isResetPassword = true;
      this.editProfileModal = true;
    },
    refreshProfileTable() {
      var self = this;
      if (!this.$route.params.id) {
        self.profileLoading = false;
        return;
      }
      self.profileLoading = true;
      self.profileError = false;
      self.profileApi
        .getListByBusinessId(this.$route.params.id)
        .then((response) => {
          self.profileItems = response.result || [];
        })
        .catch((err) => {
          console.error(err);
          self.profileError = true;
        })
        .finally(() => {
          self.profileLoading = false;
        });
    },

    isNewApplication() {
      if (this.$route.params.id) return false;
      return true;
    },
    onStateChange() {
      this.selectedDistrict = ""; // Reset district selection when state changes
    },
    uploaded(data) {
      this.obj.logoUrl = "/Documents/File/" + data.uploadedFiles[0].id;
    },
    chop_uploaded(data) {
      this.obj.companyChopUrl = "/Documents/File/" + data.uploadedFiles[0].id;
    },
    getChopImageUrl() {
      return this.removeTrailingSlash(apiUrl) + this.obj.companyChopUrl;
    },
    getImageUrl() {
      return this.removeTrailingSlash(apiUrl) + this.obj.logoUrl;
    },
    cancel() {
      this.$router.push({ path: "/tenants/BusinessList" });
    },

    submit() {
      if (this.saving) return;
      this.onSubmit();
      this.submitted = true;
    },
    onSubmit() {
      this.saving = true;
      if (!this.obj.id) {
        this.api
          .createByCurrentTenant(this.obj)
          .then((response) => {
            this.obj = response.result;
            this.$toast.success("Business added");
            this.$router.push({ path: `/tenants/Business/${this.obj.id}` });
          })
          .catch(({ data }) => {
            this.$toast.error(
              `Couldn't add the business. ${helper.getErrorMessage(data)}`
            );
          })
          .finally(() => {
            this.saving = false;
          });
      } else {
        this.api
          .update(this.obj)
          .then(() => {
            this.$toast.success("Business details saved");
            this.resetObj();
          })
          .catch(({ data }) => {
            this.$toast.error(
              `Couldn't save the business details. ${helper.getErrorMessage(data)}`
            );
          })
          .finally(() => {
            this.saving = false;
          });
      }
    },
    resetObj() {
      if (this.$route.params.id) {
        this.api
          .get(this.$route.params.id)
          .then((response) => {
            this.obj = response.result;

            // Date-only value for the HTML5 date input (YYYY-MM-DD), no timezone drift
            if (this.obj.dateIncorporation) {
              this.obj.dateIncorporation = asDateString(this.obj.dateIncorporation);
            }
          })
          .catch(({ data }) => {
            this.$toast.error(
              `Couldn't load the business. ${helper.getErrorMessage(data)}`
            );
          });
      } else {
        this.obj = this.getEmptyObj();
        this.api
          .getDefaultLogoUrl()
          .then((response) => {
            this.obj.logoUrl = response.result;
          })
          .catch((err) => {
            console.error(err);
          });
      }
    },
    getEmptyObj() {
      return {
        currency: "RM",
        country: "Malaysia",
        state: "Selangor",
        district: "Hulu Langat",
        logoUrl: "",
        brnOld: "",
        brnNew: "",
        sstNumber: "",
        sstInputTaxClaimable: false,
        dateIncorporation: null,
        msicCode: "",
        autoGeneratePayslipOnApproval: false,
      };
    },
    removeTrailingSlash(str) {
      return str.endsWith("/") ? str.slice(0, -1) : str;
    },
  },
};
</script>
